import React, { useState, useRef, useMemo } from 'react';
import { Tache, Structure } from '../types';

interface GlobalGanttChartProps {
  taches: Tache[];
  structures: Structure[];
  isLoading?: boolean;
}

type ZoomLevel = 'day' | 'week' | 'month' | 'year';
type GroupBy = 'structure' | 'procedure';

const ETAT_COLOR: Record<string, string> = {
  terminee:   'bg-green-500',
  en_cours:   'bg-blue-500',
  en_attente: 'bg-amber-400',
};
const ETAT_LABEL: Record<string, string> = {
  terminee: 'Terminée', en_cours: 'En cours', en_attente: 'En attente',
};
const COMPLEXITE_LABEL: Record<string, string> = {
  facile: 'Facile', moyen: 'Moyen', difficile: 'Difficile',
};

function getCalendarStart(date: Date, zoom: ZoomLevel): Date {
  const d = new Date(date); d.setHours(0, 0, 0, 0);
  switch (zoom) {
    case 'day': return d;
    case 'week': { const dow = d.getDay(); d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1)); return d; }
    case 'month': d.setDate(1); return d;
    case 'year':  d.setMonth(0, 1); return d;
  }
}
function getNextColStart(date: Date, zoom: ZoomLevel): Date {
  const d = new Date(date);
  switch (zoom) {
    case 'day':   d.setDate(d.getDate() + 1); break;
    case 'week':  d.setDate(d.getDate() + 7); break;
    case 'month': d.setMonth(d.getMonth() + 1); break;
    case 'year':  d.setFullYear(d.getFullYear() + 1); break;
  }
  return d;
}
function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

const UNIT_WIDTH: Record<ZoomLevel, number> = { day: 52, week: 110, month: 38, year: 10 };
const LEFT_COL = 320;

const GlobalGanttChart: React.FC<GlobalGanttChartProps> = ({ taches, structures, isLoading }) => {
  const [zoom, setZoom] = useState<ZoomLevel>('month');
  const [groupBy, setGroupBy] = useState<GroupBy>('structure');
  const [filterStructure, setFilterStructure] = useState<string>('');
  const [hovered, setHovered] = useState<Tache | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() =>
    filterStructure ? taches.filter(t => t.structureId === filterStructure) : taches,
    [taches, filterStructure]
  );

  const { minDate, maxDate } = useMemo(() => {
    if (filtered.length === 0) {
      const t = new Date(); return { minDate: t, maxDate: new Date(t.getTime() + 90*864e5) };
    }
    const dates = filtered.flatMap(t => [new Date(t.dateDebut), new Date(t.dateFin)]);
    const min = new Date(Math.min(...dates.map(d => d.getTime())));
    const max = new Date(Math.max(...dates.map(d => d.getTime())));
    const margin = (max.getTime() - min.getTime()) * 0.05;
    min.setTime(min.getTime() - margin);
    max.setTime(max.getTime() + margin);
    return { minDate: min, maxDate: max };
  }, [filtered]);

  const timeColumns = useMemo(() => {
    const cols: { date: Date; label: string; isWeekend?: boolean }[] = [];
    const cur = getCalendarStart(new Date(minDate), zoom);
    while (cur <= maxDate) {
      const isWeekend = cur.getDay() === 0 || cur.getDay() === 6;
      switch (zoom) {
        case 'day':
          cols.push({ date: new Date(cur), label: cur.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }), isWeekend });
          cur.setDate(cur.getDate() + 1); break;
        case 'week':
          cols.push({ date: new Date(cur), label: `S${getWeekNumber(cur)} ${cur.toLocaleDateString('fr-FR', { month: 'short' })}` });
          cur.setDate(cur.getDate() + 7); break;
        case 'month':
          cols.push({ date: new Date(cur), label: cur.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }) });
          cur.setMonth(cur.getMonth() + 1); break;
        case 'year':
          cols.push({ date: new Date(cur), label: cur.getFullYear().toString() });
          cur.setFullYear(cur.getFullYear() + 1); break;
      }
    }
    return cols;
  }, [minDate, maxDate, zoom]);

  const unitW = UNIT_WIDTH[zoom];
  const totalW = timeColumns.length * unitW;

  const dateToPixel = (date: Date): number => {
    for (let i = 0; i < timeColumns.length; i++) {
      const colStart = timeColumns[i].date;
      const colEnd = i + 1 < timeColumns.length ? timeColumns[i + 1].date : getNextColStart(timeColumns[i].date, zoom);
      const dur = colEnd.getTime() - colStart.getTime();
      if (date.getTime() < colEnd.getTime()) {
        const frac = dur > 0 ? (date.getTime() - colStart.getTime()) / dur : 0;
        return (i + Math.max(0, frac)) * unitW;
      }
    }
    return timeColumns.length * unitW;
  };

  const getPos = (t: Tache) => ({
    left: `${dateToPixel(new Date(t.dateDebut))}px`,
    width: `${Math.max(dateToPixel(new Date(t.dateFin)) - dateToPixel(new Date(t.dateDebut)), 12)}px`,
  });

  // Groupement
  type Group = { key: string; label: string; sublabel?: string | null; taches: Tache[] };
  const groups = useMemo((): Group[] => {
    if (groupBy === 'structure') {
      const map = new Map<string, Tache[]>();
      filtered.forEach(t => {
        const k = t.structureId || 'unknown';
        if (!map.has(k)) map.set(k, []);
        map.get(k)!.push(t);
      });
      return Array.from(map.entries()).map(([k, ts]) => ({
        key: k,
        label: ts[0]?.structureSigle || ts[0]?.structureNom || 'Structure inconnue',
        sublabel: ts[0]?.structureSigle ? ts[0]?.structureNom : undefined,
        taches: ts,
      }));
    } else {
      const map = new Map<string, Tache[]>();
      filtered.forEach(t => {
        const k = t.procedureId;
        if (!map.has(k)) map.set(k, []);
        map.get(k)!.push(t);
      });
      return Array.from(map.entries()).map(([k, ts]) => ({
        key: k,
        label: ts[0]?.procedureNom || 'Procédure inconnue',
        sublabel: ts[0]?.structureSigle || ts[0]?.structureNom,
        taches: ts,
      }));
    }
  }, [filtered, groupBy]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();
    containerRef.current.scrollLeft = scrollLeft - (e.pageX - containerRef.current.offsetLeft - startX) * 1.5;
  };
  const handleMouseUp = () => setIsDragging(false);

  const todayPx = dateToPixel(new Date());

  if (isLoading) return (
    <div className="flex items-center justify-center py-24 text-gray-400">
      <svg className="animate-spin h-8 w-8 text-primary-500 mr-3" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
      </svg>
      <span className="text-sm">Chargement des tâches…</span>
    </div>
  );

  if (filtered.length === 0) return (
    <div className="flex flex-col items-center justify-center py-20 text-gray-400">
      <svg className="h-12 w-12 mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
      <p className="text-sm">Aucune tâche à afficher</p>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50/60">
        {/* Filtre structure */}
        <select
          value={filterStructure}
          onChange={e => setFilterStructure(e.target.value)}
          className="text-sm border border-gray-200 rounded-xl px-3 py-1.5 bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">Toutes les structures</option>
          {structures.map(s => (
            <option key={s.id} value={s.id}>{s.sigle || s.nom}</option>
          ))}
        </select>

        {/* Regrouper par */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
          {(['structure', 'procedure'] as GroupBy[]).map(g => (
            <button key={g} onClick={() => setGroupBy(g)}
              className={`px-3 py-1 text-xs font-medium rounded-lg transition-all ${groupBy === g ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              Par {g === 'structure' ? 'structure' : 'procédure'}
            </button>
          ))}
        </div>

        {/* Zoom */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 ml-auto">
          {(['day', 'week', 'month', 'year'] as ZoomLevel[]).map(z => (
            <button key={z} onClick={() => setZoom(z)}
              className={`px-3 py-1 text-xs font-medium rounded-lg transition-all ${zoom === z ? 'bg-white shadow-sm text-primary-700' : 'text-gray-500 hover:text-gray-700'}`}>
              {z === 'day' ? 'Jour' : z === 'week' ? 'Semaine' : z === 'month' ? 'Mois' : 'Année'}
            </button>
          ))}
        </div>

        {/* Légende */}
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {Object.entries(ETAT_LABEL).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1">
              <span className={`w-2.5 h-2.5 rounded-sm ${ETAT_COLOR[k]}`} />{v}
            </span>
          ))}
          <span className="flex items-center gap-1 ml-1">
            <span className="w-0.5 h-4 bg-red-400 rounded" />Aujourd'hui
          </span>
        </div>
      </div>

      {/* Stats rapides */}
      <div className="flex items-center gap-6 px-5 py-2.5 border-b border-gray-50 bg-white text-xs text-gray-500">
        <span><span className="font-semibold text-gray-900">{filtered.length}</span> tâches</span>
        <span><span className="font-semibold text-green-600">{filtered.filter(t => t.etat === 'terminee').length}</span> terminées</span>
        <span><span className="font-semibold text-blue-600">{filtered.filter(t => t.etat === 'en_cours').length}</span> en cours</span>
        <span><span className="font-semibold text-red-500">{filtered.filter(t => t.enRetard).length}</span> en retard</span>
        <span><span className="font-semibold text-gray-700">{groups.length}</span> {groupBy === 'structure' ? 'structures' : 'procédures'}</span>
      </div>

      {/* Gantt scrollable */}
      <div
        ref={containerRef}
        className={`overflow-auto ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        style={{ maxHeight: '70vh' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div style={{ minWidth: `${LEFT_COL + totalW}px` }}>
          {/* Header timeline */}
          <div className="flex border-b border-gray-200 bg-gray-100 sticky top-0 z-20">
            <div className="flex-shrink-0 border-r border-gray-200 bg-gray-100 sticky left-0 z-30 flex items-center px-3"
              style={{ width: LEFT_COL, minWidth: LEFT_COL }}>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tâche</span>
            </div>
            <div className="flex relative" style={{ width: totalW }}>
              {/* Ligne "aujourd'hui" dans le header */}
              <div className="absolute top-0 bottom-0 w-0.5 bg-red-400 z-10 pointer-events-none"
                style={{ left: todayPx }} />
              {timeColumns.map((col, i) => (
                <div key={i}
                  className={`flex-shrink-0 text-center text-[10px] font-medium py-2 border-r border-gray-200 ${col.isWeekend ? 'bg-gray-200/60 text-gray-400' : 'text-gray-500'}`}
                  style={{ width: unitW, minWidth: unitW }}>
                  {col.label}
                </div>
              ))}
            </div>
          </div>

          {/* Groupes */}
          {groups.map((group, gi) => (
            <React.Fragment key={group.key}>
              {/* En-tête de groupe */}
              <div className="flex border-b border-gray-200 bg-gray-50 sticky left-0 z-10">
                <div className="flex items-center gap-2 px-3 py-2 border-r border-gray-200 bg-gray-50 sticky left-0 z-20"
                  style={{ width: LEFT_COL, minWidth: LEFT_COL }}>
                  <div className={`w-1.5 h-5 rounded-full flex-shrink-0 ${gi % 6 === 0 ? 'bg-primary-500' : gi % 6 === 1 ? 'bg-emerald-500' : gi % 6 === 2 ? 'bg-violet-500' : gi % 6 === 3 ? 'bg-amber-500' : gi % 6 === 4 ? 'bg-rose-500' : 'bg-cyan-500'}`} />
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-gray-800 truncate">{group.label}</p>
                    {group.sublabel && <p className="text-[10px] text-gray-400 truncate">{group.sublabel}</p>}
                  </div>
                  <span className="ml-auto flex-shrink-0 text-[10px] font-medium text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded-full">
                    {group.taches.length}
                  </span>
                </div>
                <div className="flex-1 relative" style={{ height: 32 }}>
                  <div className="absolute top-0 bottom-0 w-0.5 bg-red-400/60 z-10 pointer-events-none"
                    style={{ left: todayPx }} />
                  {timeColumns.map((col, i) => (
                    <div key={i} className={`absolute top-0 bottom-0 border-r border-gray-200 ${col.isWeekend ? 'bg-gray-100/60' : ''}`}
                      style={{ left: i * unitW, width: unitW }} />
                  ))}
                </div>
              </div>

              {/* Lignes de tâches */}
              {group.taches.map((tache, ti) => {
                const pos = getPos(tache);
                const barColor = ETAT_COLOR[tache.etat] || 'bg-gray-400';
                return (
                  <div key={tache.id}
                    className={`flex border-b border-gray-100 hover:bg-blue-50/20 transition-colors ${ti % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                    {/* Colonne info tâche */}
                    <div className="flex items-center gap-2 px-3 py-1.5 border-r border-gray-100 bg-inherit sticky left-0 z-10"
                      style={{ width: LEFT_COL, minWidth: LEFT_COL }}>
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${barColor}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-800 truncate">{tache.nom}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {groupBy === 'structure' && tache.procedureNom && (
                            <span className="text-[10px] text-gray-400 truncate max-w-[140px]">{tache.procedureNom}</span>
                          )}
                          {groupBy === 'procedure' && (tache.structureSigle || tache.structureNom) && (
                            <span className="text-[10px] text-gray-400 truncate max-w-[140px]">{tache.structureSigle || tache.structureNom}</span>
                          )}
                          <span className={`text-[9px] px-1 py-0.5 rounded font-medium flex-shrink-0
                            ${tache.complexite === 'difficile' ? 'bg-red-50 text-red-500' : tache.complexite === 'facile' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'}`}>
                            {COMPLEXITE_LABEL[tache.complexite]}
                          </span>
                          {tache.enRetard && (
                            <span className="text-[9px] bg-red-100 text-red-600 px-1 py-0.5 rounded font-medium flex-shrink-0">Retard</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Zone Gantt */}
                    <div className="relative flex items-center" style={{ width: totalW, minWidth: totalW, height: 44 }}>
                      {/* Grille */}
                      <div className="absolute inset-0 flex pointer-events-none">
                        <div className="absolute top-0 bottom-0 w-0.5 bg-red-400/40 z-10"
                          style={{ left: todayPx }} />
                        {timeColumns.map((col, i) => (
                          <div key={i} className={`absolute top-0 bottom-0 border-r border-gray-100 ${col.isWeekend ? 'bg-gray-100/40' : ''}`}
                            style={{ left: i * unitW, width: unitW }} />
                        ))}
                      </div>
                      {/* Barre */}
                      <div
                        className={`absolute h-6 rounded-md shadow-sm ${barColor} cursor-pointer hover:brightness-110 hover:shadow-md transition-all duration-150 z-10`}
                        style={{ left: pos.left, width: pos.width, minWidth: 14 }}
                        onMouseEnter={e => {
                          const r = e.currentTarget.getBoundingClientRect();
                          setTooltipPos({ x: r.left + r.width / 2, y: r.top - 8 });
                          setHovered(tache);
                        }}
                        onMouseLeave={() => setHovered(null)}
                      >
                        {parseInt(pos.width) > 40 && (
                          <span className="px-1.5 text-[10px] text-white font-medium leading-6 truncate block">
                            {tache.nom}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Tooltip */}
      {hovered && (
        <div className="fixed z-50 bg-white rounded-xl shadow-xl border border-gray-200 p-4 w-72 pointer-events-none text-sm"
          style={{ left: Math.min(tooltipPos.x, window.innerWidth - 300), top: tooltipPos.y, transform: 'translate(-50%, -100%)' }}>
          <p className="font-semibold text-gray-900 mb-2 leading-tight">{hovered.nom}</p>
          <div className="space-y-1.5 text-xs text-gray-600">
            {hovered.procedureNom && <p><span className="text-gray-400">Procédure :</span> {hovered.procedureNom}</p>}
            {(hovered.structureSigle || hovered.structureNom) && (
              <p><span className="text-gray-400">Structure :</span> {hovered.structureSigle || hovered.structureNom}</p>
            )}
            <div className="flex items-center gap-2 pt-1">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold text-white ${ETAT_COLOR[hovered.etat]}`}>
                {ETAT_LABEL[hovered.etat]}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium
                ${hovered.complexite === 'difficile' ? 'bg-red-50 text-red-600' : hovered.complexite === 'facile' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                {COMPLEXITE_LABEL[hovered.complexite]}
              </span>
              {hovered.enRetard && <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">En retard</span>}
            </div>
            <div className="grid grid-cols-2 gap-1 pt-1 border-t border-gray-100 mt-1">
              <p><span className="text-gray-400">Début :</span> {new Date(hovered.dateDebut).toLocaleDateString('fr-FR')}</p>
              <p><span className="text-gray-400">Fin :</span> {new Date(hovered.dateFin).toLocaleDateString('fr-FR')}</p>
            </div>
            {(hovered.assignedUserPrenom || hovered.assignedUserNom) && (
              <p className="pt-1"><span className="text-gray-400">Assigné :</span> {hovered.assignedUserPrenom} {hovered.assignedUserNom}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalGanttChart;
